import React, { Component, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import find from 'lodash/find';
import values from 'lodash/values';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import {
    SIZE_BLOCK,
    STYLE_NEUTRAL,
} from 'eventbrite_design_system/button/constants';
import Tag from 'eventbrite_design_system/tag/Tag';
import Icon from 'eventbrite_design_system/icon/Icon';
import FilterChunkySvg from 'eventbrite_design_system/iconography/icons/FilterChunky';
import ChevronDownChunky from 'eventbrite_design_system/iconography/icons/ChevronDownChunky';
import ChevronUpChunky from 'eventbrite_design_system/iconography/icons/ChevronUpChunky';
import Badge from 'eventbrite_design_system/badge/Badge';

import { HAS_WINDOW } from 'js-utils/feature-detection';

import { useScrollWatcher } from '../../../hooks';

import {
    CATEGORY_FILTER,
    DATE_FILTER,
    FORMAT_FILTER,
    PRICE_FILTER,
    LANGUAGE_FILTER,
    CURRENCY_FILTER,
    TAG_BACKGROUND_COLOR,
    TAG_STYLE,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    CLEAR_PRICE_FILTER,
    HEADER_HEIGHT,
} from '../../constants';

import BotFilters from '../filterPanel/BotFilters';

import './mobileFilterHeader.scss';

const MOBILE_FILTER_HEADER_VERT_OFFSET = 16;

const getRenderedFilters = ({
    date,
    format,
    price,
    category,
    language,
    currency,
    onClose,
    onActiveToggle,
    filterTypeToShow,
}) => {
    const filterObject = {
        [DATE_FILTER]: date || '',
        [CATEGORY_FILTER]: category || '',
        [FORMAT_FILTER]: format || '',
        [PRICE_FILTER]: price || CLEAR_PRICE_FILTER,
        [LANGUAGE_FILTER]: language || '',
        [CURRENCY_FILTER]: currency || '',
    };

    const filtersToShow = Object.keys(filterObject).sort((a, b) => {
        const valA =
            !filterObject[a] || filterObject[a] === CLEAR_PRICE_FILTER ? 1 : 0;
        const valB =
            !filterObject[b] || filterObject[b] === CLEAR_PRICE_FILTER ? 1 : 0;

        return valB - valA;
    });

    return filtersToShow.map((key) => {
        // Given an applied filters value, grab the appropriate
        // display value associated to that filter
        const dropdownVals =
            FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].values;
        const label = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].label;

        const foundFilter = find(
            dropdownVals,
            ({ value }) =>
                value === filterObject[key] ||
                (Array.isArray(filterObject[key]) &&
                    filterObject[key][0] === value),
        );

        const filterIsShowing = filterTypeToShow === key;
        const iconType = filterIsShowing ? (
            <ChevronUpChunky />
        ) : (
            <ChevronDownChunky />
        );

        let display;

        if (Array.isArray(filterObject[key])) {
            display = `${foundFilter.display}${
                filterObject[key].length > 1
                    ? ` + ${filterObject[key].length - 1}`
                    : ''
            }`;
        } else if (foundFilter) {
            display = foundFilter.display;
        } else {
            // This is for handling pick a date, since it is variable
            // it doesn't show up in the FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP array
            display = date;
        }

        let isClosable = true;

        if (!filterObject[key]) {
            isClosable = false;
        }

        if (key === PRICE_FILTER && !price) {
            isClosable = false;
        }

        return (
            <span
                className={`facet facet--${key} eds-l-pad-left-1`}
                key={key}
                data-integration="facet"
            >
                <Tag
                    initialIsActive={false}
                    key={key}
                    isClosable={isClosable}
                    isStatic={isClosable}
                    backgroundColor={
                        isClosable ? TAG_BACKGROUND_COLOR : undefined
                    }
                    onClose={onClose.bind(null, key)}
                    onActiveToggle={() => onActiveToggle(key)}
                    style={TAG_STYLE}
                >
                    <span className="tag-content">
                        <span className="eds-l-pad-right-1">
                            {isClosable ? display : label}
                        </span>
                        {!isClosable && (
                            <Icon
                                type={iconType}
                                color="grey-700"
                                size="small"
                            />
                        )}
                    </span>
                </Tag>
            </span>
        );
    });
};

const MoreFiltersToggle = ({ onClick, spacingClass, filters }) => {
    const btnProps = {
        onClick,
        style: STYLE_NEUTRAL,
        size: SIZE_BLOCK,
    };

    const numberActiveFilters = uniq(compact(values(filters))).length;
    const isFilterActive = !!numberActiveFilters;

    return (
        <span
            className={`${spacingClass} more-filters`}
            data-integration="more-filters-facet"
        >
            <Tag
                initialIsActive={false}
                onActiveToggle={btnProps.onClick}
                backgroundColor={
                    isFilterActive ? TAG_BACKGROUND_COLOR : undefined
                }
                style={TAG_STYLE}
            >
                <span className="tag-content eds-text-weight--heavy">
                    <Badge
                        count={numberActiveFilters}
                        bgColor="white"
                        contentColor="ui-blue"
                    >
                        <Icon
                            type={<FilterChunkySvg />}
                            color={isFilterActive ? 'white' : 'grey-700'}
                            size="small"
                        />
                    </Badge>
                </span>
            </Tag>
        </span>
    );
};

class MobileFilterHeader extends Component {
    static propTypes = {
        trackGAEvent: PropTypes.func,
        handleApplyFilter: PropTypes.func,
        handleRemoveFilter: PropTypes.func,
        selectedCategory: PropTypes.string,
        selectedPrice: PropTypes.string,
        selectedDate: PropTypes.string,
        selectedFormat: PropTypes.string,
        isBotRequest: PropTypes.bool,
        locationSlug: PropTypes.string,
        facets: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                count: PropTypes.number,
                displayName: PropTypes.string,
            }),
        ),
        isLoading: PropTypes.bool,
        showFocusDrawer: PropTypes.func,
        closeFocusDrawer: PropTypes.func,
        addFocusDrawerNotification: PropTypes.func,
        hideFocusDrawerNotification: PropTypes.func,
        /**
         * Callback to execute when a filter is selected
         */
        openCloseAnimatedDrawer: PropTypes.func,
        /**
         * Type of individual filter active in the filter list
         */
        filterTypeToShow: PropTypes.string,
    };

    constructor(props) {
        super(props);
        const {
            selectedCategory,
            selectedDate,
            selectedFormat,
            selectedPrice,
            selectedCurrency,
            selectedLanguage,
        } = props;

        this.state = {
            [CURRENCY_FILTER]: selectedCurrency,
            [LANGUAGE_FILTER]: selectedLanguage,
            [DATE_FILTER]: selectedDate,
            [CATEGORY_FILTER]: selectedCategory,
            [FORMAT_FILTER]: selectedFormat,
            [PRICE_FILTER]: selectedPrice,
            [`initial${CATEGORY_FILTER}`]: selectedCategory,
            [`initial${FORMAT_FILTER}`]: selectedFormat,
            [`initial${PRICE_FILTER}`]: selectedPrice,
            [`initial${CURRENCY_FILTER}`]: selectedCurrency,
            [`initial${LANGUAGE_FILTER}`]: selectedLanguage,
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({
        selectedCategory: newCategory,
        selectedPrice: newPrice,
        selectedDate: newDate,
        selectedFormat: newFormat,
        selectedCurrency: newCurrency,
        selectedLanguage: newLanguage,
    }) {
        this.setState(
            ({
                [DATE_FILTER]: currentDate,
                [PRICE_FILTER]: currentPrice,
                [CATEGORY_FILTER]: currentCategory,
                [FORMAT_FILTER]: currentFormat,
                [CURRENCY_FILTER]: currentCurrency,
                [LANGUAGE_FILTER]: currentLanguage,
            }) => {
                let price = currentPrice;
                let category = currentCategory;
                let date = currentDate;
                let format = currentFormat;
                let language = currentLanguage;
                let currency = currentCurrency;

                if (currentDate !== newDate) {
                    date = newDate;
                }

                if (currentCategory !== newCategory) {
                    category = newCategory;
                }

                if (currentPrice !== newPrice) {
                    price = newPrice;
                }

                if (currentFormat !== newFormat) {
                    format = newFormat;
                }

                if (currentLanguage !== newLanguage) {
                    language = newLanguage;
                }

                if (currentCurrency !== newCurrency) {
                    currency = newCurrency;
                }

                return {
                    [DATE_FILTER]: date,
                    [PRICE_FILTER]: price,
                    [CATEGORY_FILTER]: category,
                    [FORMAT_FILTER]: format,
                    [LANGUAGE_FILTER]: language,
                    [CURRENCY_FILTER]: currency,
                    [`initial${CATEGORY_FILTER}`]: category,
                    [`initial${FORMAT_FILTER}`]: format,
                    [`initial${PRICE_FILTER}`]: price,
                    [`initial${CURRENCY_FILTER}`]: currency,
                    [`initial${LANGUAGE_FILTER}`]: language,
                    hasMoreFilters: price || category || format,
                };
            },
        );
    }

    _handleRemoveFilter(filterType) {
        const { handleRemoveFilter } = this.props;

        this.setState({ [filterType]: '' }, () => {
            handleRemoveFilter({ [filterType]: true });
        });
    }

    render() {
        const {
            selectedDate,
            selectedCategory,
            selectedPrice,
            selectedFormat,
            openCloseAnimatedDrawer,
            filterTypeToShow,
            selectedLanguage,
            selectedCurrency,
        } = this.props;

        const handleOpenFilterPanel = (filterType) =>
            openCloseAnimatedDrawer(true, filterType);

        const appliedFiltersAsFacets = getRenderedFilters({
            date: selectedDate,
            format: selectedFormat,
            price: selectedPrice,
            category: selectedCategory,
            language: selectedLanguage,
            currency: selectedCurrency,
            onClose: this._handleRemoveFilter.bind(this),
            onActiveToggle: handleOpenFilterPanel,
            filterTypeToShow: filterTypeToShow,
        });

        return (
            <>
                <MoreFiltersToggle
                    onClick={() => openCloseAnimatedDrawer(true)}
                    spacingClass="eds-show-down-sw"
                    filters={this.state}
                />
                {[...appliedFiltersAsFacets]}
            </>
        );
    }
}

const MobileFilterHeaderContainer = (props) => {
    const { isBotRequest, locationSlug } = props;
    const classes = classNames('mobile-filter-header');
    const scrollablePaneClasses = classNames(
        'mobile-filter-header__facets',
        // Give scrollbar some space
        'eds-l-pad-bot-4',
    );

    return (
        <section className={classes} data-spec="mobile-filter-header">
            <div className={scrollablePaneClasses}>
                {isBotRequest ? (
                    <BotFilters slug={locationSlug} />
                ) : (
                    <MobileFilterHeader {...props} />
                )}
            </div>
        </section>
    );
};

const MobileFilterHeaderWithScrollWatcher = (props) => {
    let isMobile = true;

    if (HAS_WINDOW) {
        const mediaQuery = window.matchMedia('(min-width: 822px)');
        isMobile = !mediaQuery.matches;
    }

    const [isSticky, setIsSticky] = useState(false);
    const [
        { componentYOffset, componentHeight },
        setComponentDimensions,
    ] = useState({});

    // Generate ref on the search bar to determine how
    // much of an offset should be generated based on the
    // amount of content above it.
    const facetedSearchBar = useCallback((node) => {
        if (node !== null && node.getBoundingClientRect) {
            if (!componentYOffset) {
                const dimensions = node.getBoundingClientRect();

                setComponentDimensions({
                    componentYOffset:
                        node.offsetTop -
                        MOBILE_FILTER_HEADER_VERT_OFFSET +
                        HEADER_HEIGHT,
                    componentHeight: dimensions.height,
                });
            }
        }
    });

    useScrollWatcher(
        (pos) => {
            const shouldStick = pos > componentYOffset;
            if (isSticky !== shouldStick) {
                setIsSticky(shouldStick);
            }
        },
        {
            deps: [isSticky, componentYOffset],
            interval: 10,
        },
    );

    return isMobile && !props.isBotRequest ? (
        <div style={{ paddingTop: isSticky ? `${componentHeight}px` : '0' }}>
            <div
                ref={facetedSearchBar}
                className={`${isSticky ? 'mobile-filter-header--sticky' : ''}`}
            >
                <MobileFilterHeaderContainer {...props} />
            </div>
        </div>
    ) : (
        <MobileFilterHeaderContainer {...props} />
    );
};

export default MobileFilterHeaderWithScrollWatcher;
